/**
 * br-motion-pause — the shared pause / play control (WCAG 2.2.2).
 *
 * One control per moving band or strip. Markup contract:
 *
 *   <… data-br-motion-pause-scope>
 *     <button type="button" class="br-motion-pause …"
 *       data-br-motion-pause="video|animation"
 *       data-label-pause="Pause video" data-label-play="Play video">
 *       <svg data-br-motion-pause-icon="pause">…</svg>
 *       <svg data-br-motion-pause-icon="play" hidden>…</svg>
 *     </button>
 *
 * The button is laid out from first paint but `visibility: hidden` until
 * the script marks it data-br-motion-pause-ready (no layout shift, no
 * dead control without JS). The script swaps its accessible name and
 * glyph and writes data-br-motion-paused="true|false" on the scope. No
 * aria-pressed: a swapping name plus a pressed state reads ambiguously.
 *
 *   video      controls the first <video> in the scope through
 *              pause() / play(). An optional data-br-motion-pause-area
 *              element in the scope (the media wrapper) is a POINTER-ONLY
 *              affordance: a click or tap anywhere on it toggles too. It
 *              is not focusable and adds no tab stop; keyboard users have
 *              the button. The displayed state follows the
 *              video's real `paused` flag, so a blocked autoplay shows
 *              Play. A user pause is held: any later play() from other
 *              code (a section's own sync) is paused again at once.
 *   animation  the scope's CSS owns the motion and keys
 *              `animation-play-state` off the scope attribute.
 *
 * State lives on the element for as long as the page is open; nothing
 * here observes scroll, so scrolling away and back never restarts it.
 *
 * While a video is paused and has never played (autoplay blocked, or
 * held by reduced motion) the scope carries data-br-motion-pause-unstarted
 * and the CSS fades the video out so WebKit's own centred start-playback
 * glyph cannot show; the section's poster <img> behind it shows instead.
 *
 * Reduced motion: the control starts in the Play state and the motion
 * starts stopped — the user may start it. Once the user has pressed the
 * control, their choice stands: a later change of the OS setting never
 * overrides it.
 *
 * Deferred sources (<source data-src>, br-video-defer) are NOT handled:
 * pairing this control with a deferred video needs a brVideoDefer method
 * to promote on an explicit Play (it never promotes under reduced motion).
 *
 * Idempotent; re-inits on shopify:section:load and releases its
 * listeners on shopify:section:unload.
 */
(() => {
  if (window.brMotionPause) {
    window.brMotionPause.init(document);
    return;
  }

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const instances = new Map();

  const setup = (button) => {
    if (instances.has(button)) return;

    const scope = button.closest('[data-br-motion-pause-scope]');
    if (!scope) return;

    const mode = button.dataset.brMotionPause === 'video' ? 'video' : 'animation';
    const video = mode === 'video' ? scope.querySelector('video') : null;
    if (mode === 'video' && !video) return;

    const controller = new AbortController();
    const { signal } = controller;
    instances.set(button, controller);

    const pauseIcon = button.querySelector('[data-br-motion-pause-icon="pause"]');
    const playIcon = button.querySelector('[data-br-motion-pause-icon="play"]');
    const labels = { pause: button.dataset.labelPause, play: button.dataset.labelPlay };

    // Whether the motion SHOULD be stopped: the reduced-motion default
    // until the user decides, then the user's last choice.
    let wantPaused = reducedMotion.matches;
    let userChose = false;

    const render = () => {
      const paused = video ? video.paused : wantPaused;
      scope.dataset.brMotionPaused = paused ? 'true' : 'false';
      if (video) {
        scope.toggleAttribute(
          'data-br-motion-pause-unstarted',
          paused && video.played.length === 0
        );
      }
      if (pauseIcon) pauseIcon.toggleAttribute('hidden', paused);
      if (playIcon) playIcon.toggleAttribute('hidden', !paused);
      const label = paused ? labels.play : labels.pause;
      if (label) button.setAttribute('aria-label', label);
    };

    const apply = () => {
      if (video) {
        if (wantPaused) {
          video.removeAttribute('autoplay');
          video.pause();
        } else {
          const attempt = video.play();
          if (attempt && typeof attempt.catch === 'function') attempt.catch(render);
        }
      }
      render();
    };

    const toggle = () => {
      userChose = true;
      wantPaused = video ? !video.paused : !wantPaused;
      apply();
    };

    button.addEventListener('click', toggle, { signal });

    const area = video ? scope.querySelector('[data-br-motion-pause-area]') : null;
    if (area) {
      const isControl = (event) =>
        !!event.target.closest('a, button, input, select, textarea, summary, [tabindex]');
      // WebKit draws its own start-playback glyph on a video whose
      // autoplay was blocked; its handler runs at the video (target phase)
      // and starts playback BEFORE this bubbling listener sees the click.
      // The state is therefore read in the capture phase, so the click
      // means the same thing whichever glyph it landed on.
      let pausedAtPress = null;
      area.addEventListener(
        'click',
        (event) => {
          pausedAtPress = isControl(event) ? null : video.paused;
        },
        { capture: true, signal }
      );
      area.addEventListener(
        'click',
        () => {
          // Only the media itself: anything interactive layered over it
          // (links, buttons) keeps its own click.
          if (pausedAtPress === null) return;
          userChose = true;
          wantPaused = !pausedAtPress;
          pausedAtPress = null;
          apply();
        },
        { signal }
      );
    }

    if (video) {
      video.addEventListener(
        'play',
        () => {
          if (wantPaused) video.pause();
          render();
        },
        { signal }
      );
      video.addEventListener('pause', render, { signal });
    }

    reducedMotion.addEventListener(
      'change',
      () => {
        if (userChose) return;
        wantPaused = reducedMotion.matches;
        apply();
      },
      { signal }
    );

    button.setAttribute('data-br-motion-pause-ready', '');

    // Without reduced motion the video is left to its own autoplay /
    // section script; only the reduced-motion stop is enforced here.
    if (wantPaused) apply();
    else render();
  };

  const init = (root) => {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-br-motion-pause]').forEach(setup);
  };

  const destroy = (root) => {
    instances.forEach((controller, button) => {
      if (root.contains(button)) {
        controller.abort();
        instances.delete(button);
      }
    });
  };

  window.brMotionPause = { init };

  init(document);

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
})();
